import type { ExternalStorageProvider } from '@/constants/constants-external-storage'

export type ExternalStorageLinkedItem = {
  external_storage?: {
    provider: ExternalStorageProvider
    tombstoned: boolean
    is_externally_managed?: boolean
  } | null
}

export function isLocalStorageItem(item: ExternalStorageLinkedItem): boolean {
  return !item.external_storage
}

/**
 * Whether an external association still controls mutations for this item.
 * Missing management state is treated as managed for frontend/backend rollout safety.
 */
export function isExternallyManagedStorageItem(item: ExternalStorageLinkedItem): boolean {
  return !!item.external_storage && item.external_storage.is_externally_managed !== false
}

/** Delete is the only Paxton mutation detached external items gain. */
export function isMatterFilesDeleteEligible(item: ExternalStorageLinkedItem): boolean {
  return isLocalStorageItem(item) || item.external_storage?.is_externally_managed === false
}

/**
 * An item with a live provider link. Tombstoned links (sync disconnected) are
 * not active — those items belong back in the regular Drive views.
 */
export function isActiveExternalStorageItem(item: ExternalStorageLinkedItem): boolean {
  return !!item.external_storage && !item.external_storage.tombstoned
}

/**
 * An item whose provider link was tombstoned by a sync disconnect. Visible in
 * Drive views. Operation-specific helpers decide which mutations are available.
 */
export function isTombstonedExternalStorageItem(item: ExternalStorageLinkedItem): boolean {
  return !!item.external_storage && item.external_storage.tombstoned
}

export function isVisibleExternalStorageItem(item: ExternalStorageLinkedItem, provider: ExternalStorageProvider | null | undefined): boolean {
  return !!provider && item.external_storage?.provider === provider && item.external_storage.tombstoned === false
}

export function filterLocalStorageItems<T extends ExternalStorageLinkedItem>(items: T[]): T[] {
  return items.filter(isLocalStorageItem)
}

export function filterVisibleExternalStorageItems<T extends ExternalStorageLinkedItem>(items: T[], provider: ExternalStorageProvider | null | undefined): T[] {
  return items.filter((item) => isVisibleExternalStorageItem(item, provider))
}
